// The real backend: Supabase with the publishable key and the signed-in person's token. Row-level security and the
// database functions decide what each person may see and do; nothing here is a security boundary.
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { Database, Json } from "./database.types";
import { type Api, ApiError, type MfaState, PAGE_SIZE, RAW_CALLS_PAGE, type SignInTarget } from "./api";
import {
  type CustomerList,
  type CustomerPage,
  type CustomerQuery,
  customerQueryJson,
  type FollowUpInput,
  type FollowUpList,
  type FollowUpQuery,
  followUpQueryJson,
  type Insights,
  type Label,
  type MessageTemplate,
  type MessageTemplateInput,
  type VisitResult,
} from "./crm";
import {
  type Disposition,
  type DispositionCall,
  type Dispositions,
  dispositionsJson,
  parseDispositions,
} from "./dispositions";
import {
  type AiInstructions,
  asBusinessType,
  type BusinessType,
  type BusinessTypeResult,
  type CallType,
  type ChecklistTally,
  parseInstructions,
  type TextField,
} from "./aiInstructions";
import type { PlatformAiUsage } from "./aiUsage";
import { addOnsOf } from "./readiness";
import { recordingDue } from "./recordingDue";
import type { UseCase } from "./integrations";
import type {
  AddOns,
  BillingSummary,
  CallDetail,
  CallNote,
  CompanyHealth,
  CompanyHealthDetail,
  CompanyTeamMember,
  DashboardRange,
  EraseResult,
  FacebookAssign,
  FacebookChosen,
  FacebookLogin,
  FacebookPageChoice,
  FacebookState,
  HealthOverview,
  ImportOptions,
  ImportResult,
  ImportRow,
  IndiaMartRead,
  IntegrationActivityItem,
  IntegrationDemand,
  IntegrationsState,
  LeadEnquiry,
  LeadImport,
  LeadListItem,
  LeadQuery,
  LiveCall,
  MyOperatorConnected,
  NoteLayer,
  ObjectionQuotes,
  Overview,
  PlatformHealth,
  PlatformOrg,
  QaCall,
  QaCheck,
  QaLayer,
  QaMe,
  QaNext,
  QaRole,
  QaStage,
  QaSummary,
  RawCallRow,
  RecentEnquiries,
  Recording,
  RecordingCounts,
  ReviewAction,
  Role,
  Stage,
  Status,
  Task,
  TaskType,
  TaskWithLead,
  TeamHealth,
  Usage,
  WebhookFields,
  WebhookRetried,
  WebhookState,
} from "./types";

type PgError = { message: string; code?: string };

function toApiError(e: PgError): ApiError {
  if (
    e.code === "PGRST301" || e.code === "PGRST303" ||
    /jwt expired|invalid jwt/i.test(e.message)
  ) {
    return new ApiError(
      "Your session ended. Please sign in again.",
      "signed_out",
    );
  }
  if (e.code === "PGRST116") {
    return new ApiError(
      "Not found, or you do not have access to it.",
      "not_found",
    );
  }
  if (e.code === "42501" && /permission denied/i.test(e.message)) {
    return new ApiError("You do not have permission to do that.", "42501");
  }
  return new ApiError(e.message, e.code);
}

type Res = { data: unknown; error: PgError | null };

/** The data of a successful response; an error, or no row where one was expected, throws. */
function must<R extends Res>(res: R): NonNullable<R["data"]> {
  if (res.error) throw toApiError(res.error);
  if (res.data === null || res.data === undefined) {
    throw new ApiError(
      "Not found, or you do not have access to it.",
      "not_found",
    );
  }
  return res.data as NonNullable<R["data"]>;
}

/** The data of a response that may legitimately be empty (maybeSingle). */
function maybe<R extends Res>(res: R): R["data"] {
  if (res.error) throw toApiError(res.error);
  return res.data;
}

/** A call that returns nothing: only the error matters. */
function done(res: { error: PgError | null }): void {
  if (res.error) throw toApiError(res.error);
}

/** An update that row-level security filtered out changes no row and returns no error: treat that as refused. */
function changedOne(
  res: { data: unknown[] | null; error: PgError | null },
): void {
  if (res.error) throw toApiError(res.error);
  if (!res.data || res.data.length === 0) {
    throw new ApiError("You do not have permission to change that.", "42501");
  }
}

/** Indian 10-digit mobiles get +91; anything else must already be in +country form. */
export function toE164(phone: string): string {
  const digits = phone.replace(/[^\d+]/g, "");
  if (/^\d{10}$/.test(digits)) return `+91${digits}`;
  if (/^0\d{10}$/.test(digits)) return `+91${digits.slice(1)}`;
  if (/^91\d{10}$/.test(digits)) return `+${digits}`;
  return digits.startsWith("+") ? digits : `+${digits}`;
}

export class SupabaseApi implements Api {
  readonly demo = false;
  private readonly db: SupabaseClient<Database>;

  constructor(url: string, publishableKey: string) {
    this.db = createClient<Database>(url, publishableKey, {
      auth: {
        flowType: "pkce",
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    });
  }

  // ---------------------------------------------------------------- session

  async hasSession(): Promise<boolean> {
    const { data } = await this.db.auth.getSession();
    return data.session !== null;
  }

  onSessionChange(listener: () => void): () => void {
    const { data } = this.db.auth.onAuthStateChange((event) => {
      if (
        event === "SIGNED_IN" || event === "SIGNED_OUT" ||
        event === "MFA_CHALLENGE_VERIFIED"
      ) listener();
    });
    return () => data.subscription.unsubscribe();
  }

  /** The phone sign-in request the last code was sent for (the phone-sign-in function, D-057). */
  private phoneRequest: { phone: string; id: string } | null = null;

  async sendCode(target: SignInTarget): Promise<void> {
    if (target.email) {
      const { error } = await this.db.auth.signInWithOtp({
        email: target.email.trim().toLowerCase(),
        options: { shouldCreateUser: true },
      });
      if (error) throw new ApiError(error.message);
      return;
    }
    // Phone codes come from MSG91 through the phone-sign-in function, and only to invited numbers (AUTH-14).
    const phone = toE164(target.phone ?? "");
    const { request } = await this.phoneSignIn<{ request: string }>({ action: "send", phone });
    this.phoneRequest = { phone, id: request };
  }

  async verifyCode(target: SignInTarget, code: string): Promise<void> {
    const token = code.replace(/\s/g, "");
    if (target.email) {
      const { error } = await this.db.auth.verifyOtp({
        email: target.email.trim().toLowerCase(),
        token,
        type: "email",
      });
      if (error) throw new ApiError(error.message);
      return;
    }
    const request = this.phoneRequest;
    if (!request || request.phone !== toE164(target.phone ?? "")) throw new ApiError("Ask for a code first.");
    // An ordinary Supabase session for the request's own number (AUTH-16).
    const session = await this.phoneSignIn<{ access_token: string; refresh_token: string }>({
      action: "verify",
      request: request.id,
      code: token,
    });
    const { error } = await this.db.auth.setSession({
      access_token: session.access_token,
      refresh_token: session.refresh_token,
    });
    if (error) throw new ApiError(error.message);
    this.phoneRequest = null;
  }

  private async phoneSignIn<T>(body: Record<string, unknown>): Promise<T> {
    const { data, error } = await this.db.functions.invoke<T>("phone-sign-in", { body });
    if (error) {
      let message = "Sign-in failed. Try again in a minute.";
      const context = (error as { context?: Response }).context;
      if (context && typeof context.json === "function") {
        const reply = await context.json().catch(() => null);
        if (reply?.error) message = reply.error;
      }
      throw new ApiError(message);
    }
    if (!data) throw new ApiError("Sign-in failed. Try again in a minute.");
    return data;
  }

  async signOut(): Promise<void> {
    await this.db.auth.signOut({ scope: "local" });
  }

  async status(): Promise<Status> {
    const res = await this.db.rpc("my_status");
    if (res.error) {
      if (res.error.code === "42501") {
        await this.db.auth.signOut({ scope: "local" });
        throw new ApiError("Please sign in.", "signed_out");
      }
      throw toApiError(res.error);
    }
    let status = res.data as unknown as Status;
    if (!status.member && !status.platform_admin) {
      // Invited, but the access-token hook has not linked the invitation yet: link it now (AUTH-05).
      const linked = await this.db.rpc("accept_pending_invitation");
      if (!linked.error && linked.data) {
        await this.db.auth.refreshSession();
        status = must(await this.db.rpc("my_status")) as unknown as Status;
      }
    }
    return status;
  }

  async refresh(): Promise<void> {
    await this.db.auth.refreshSession();
  }

  async mfaState(): Promise<MfaState> {
    const aal = await this.db.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aal.error) throw new ApiError(aal.error.message);
    const factors = await this.db.auth.mfa.listFactors();
    if (factors.error) throw new ApiError(factors.error.message);
    const verified = factors.data.totp.find((f) => f.status === "verified");
    return {
      current: aal.data.currentLevel,
      next: aal.data.nextLevel,
      verifiedFactorId: verified?.id ?? null,
    };
  }

  async mfaEnroll(): Promise<{ factorId: string; qr: string; secret: string }> {
    // Drop half-finished enrolments first, so a reload does not pile them up.
    const factors = await this.db.auth.mfa.listFactors();
    for (const f of factors.data?.all ?? []) {
      if (f.factor_type === "totp" && f.status === "unverified") {
        await this.db.auth.mfa.unenroll({ factorId: f.id });
      }
    }
    const { data, error } = await this.db.auth.mfa.enroll({
      factorType: "totp",
      friendlyName: "Voxowa admin",
    });
    if (error) throw new ApiError(error.message);
    return {
      factorId: data.id,
      qr: data.totp.qr_code,
      secret: data.totp.secret,
    };
  }

  async mfaVerify(factorId: string, code: string): Promise<void> {
    const { error } = await this.db.auth.mfa.challengeAndVerify({
      factorId,
      code: code.replace(/\s/g, ""),
    });
    if (error) throw new ApiError(error.message);
  }

  async createOrganization(
    input: { name: string; displayName: string; timezone: string },
  ): Promise<void> {
    done(
      await this.db.rpc("create_organization", {
        p_name: input.name,
        p_display_name: input.displayName,
        p_timezone: input.timezone,
      }),
    );
    await this.db.auth.refreshSession();
  }

  // ------------------------------------------------------------- org data

  async org() {
    return must(await this.db.from("organizations").select("*").single());
  }

  async members() {
    return must(
      await this.db.from("members").select("*").order("display_name"),
    );
  }

  async overview(): Promise<Overview> {
    return must(await this.db.rpc("dashboard_overview")) as unknown as Overview;
  }

  async dashboardRange(from: string, to: string): Promise<DashboardRange> {
    return must(await this.db.rpc("dashboard_range", { p_from: from, p_to: to })) as unknown as DashboardRange;
  }

  async recentEnquiries(hours = 24): Promise<RecentEnquiries> {
    return must(await this.db.rpc("recent_enquiries", { p_hours: hours })) as unknown as RecentEnquiries;
  }

  async integrationsState(): Promise<IntegrationsState> {
    return must(await this.db.rpc("integrations_state")) as unknown as IntegrationsState;
  }

  async requestIntegration(key: string, useCase: UseCase | null, note: string | null) {
    done(
      await this.db.rpc("request_integration", {
        p_key: key,
        ...(useCase ? { p_use_case: useCase } : {}),
        ...(note ? { p_note: note } : {}),
      }),
    );
  }

  async openIntegration(key: string) {
    done(await this.db.rpc("open_integration", { p_key: key }));
  }

  /** An Edge Function called as the signed-in person; its own words when it says no. */
  private async edge<T>(fn: string, body: Record<string, unknown>, fallback: string): Promise<T> {
    const { data, error } = await this.db.functions.invoke<T>(fn, { body });
    if (error) {
      let message = fallback;
      const context = (error as { context?: Response }).context;
      if (context && typeof context.json === "function") {
        const reply = await context.json().catch(() => null);
        if (reply?.error) message = reply.error;
      }
      throw new ApiError(message);
    }
    return data as T;
  }

  private async myoperator<T>(body: Record<string, unknown>, fallback: string): Promise<T> {
    return await this.edge<T>("myoperator", body, fallback);
  }

  async connectMyOperator(token: string, ivrNumbers: string[]): Promise<MyOperatorConnected> {
    return await this.myoperator<MyOperatorConnected>(
      { action: "connect", token, ivr_numbers: ivrNumbers },
      "MyOperator could not be connected.",
    );
  }

  async syncMyOperator() {
    return await this.myoperator<{ logs?: number; moved?: number; error?: string }>(
      { action: "sync" },
      "MyOperator could not be read.",
    );
  }

  async updateMyOperatorSettings(ivrNumbers: string[], userMembers: Record<string, string | null>) {
    done(await this.db.rpc("update_myoperator_settings", { p_ivr_numbers: ivrNumbers, p_user_members: userMembers }));
  }

  async disconnectIntegration(key: string) {
    done(await this.db.rpc("disconnect_integration", { p_key: key }));
  }

  async facebookState(): Promise<FacebookState> {
    return must(await this.db.rpc("facebook_lead_ads_state")) as unknown as FacebookState;
  }

  private async facebook<T>(body: Record<string, unknown>, fallback: string): Promise<T> {
    return await this.edge<T>("facebook-leads", body, fallback);
  }

  async facebookStart(redirectUri: string): Promise<string> {
    const { url } = await this.facebook<{ url: string }>(
      { action: "start", redirect_uri: redirectUri },
      "Facebook could not be opened.",
    );
    return url;
  }

  async facebookFinish(code: string, state: string): Promise<FacebookLogin> {
    return await this.facebook<FacebookLogin>(
      { action: "finish", code, state },
      "Facebook did not finish the login.",
    );
  }

  async facebookPages(): Promise<FacebookPageChoice[]> {
    return (await this.facebook<{ pages: FacebookPageChoice[] }>({ action: "pages" }, "Your Pages could not be read."))
      .pages;
  }

  async facebookChoose(pageIds: string[]): Promise<FacebookChosen[]> {
    return (await this.facebook<{ pages: FacebookChosen[] }>(
      { action: "choose", pages: pageIds },
      "The Pages could not be connected.",
    )).pages;
  }

  async facebookRemovePage(pageId: string) {
    await this.facebook({ action: "remove_page", page_id: pageId }, "The Page could not be removed.");
  }

  async facebookSetForms(pageId: string, formIds: string[] | null) {
    done(await this.db.rpc("facebook_set_forms", { p_page_id: pageId, p_form_ids: formIds }));
  }

  async facebookSetAssignment(mode: FacebookAssign["mode"], members: string[]) {
    done(await this.db.rpc("facebook_set_assignment", { p_mode: mode, p_members: members }));
  }

  async pauseIntegration(key: string) {
    done(await this.db.rpc("pause_integration", { p_key: key }));
  }

  async resumeIntegration(key: string) {
    done(await this.db.rpc("resume_integration", { p_key: key }));
  }

  async integrationActivity(key: string): Promise<IntegrationActivityItem[]> {
    return must(await this.db.rpc("integration_activity", { p_key: key })) as unknown as IntegrationActivityItem[];
  }

  async facebookSetFormAssignment(formId: string, mode: FacebookAssign["mode"] | "default", members: string[]) {
    done(
      await this.db.rpc("facebook_set_form_assignment", { p_form_id: formId, p_mode: mode, p_members: members }),
    );
  }

  async facebookImport(days: 7 | 30 | 90, assign: boolean) {
    await this.facebook({ action: "import", days, assign }, "The import could not start.");
  }

  async facebookSync() {
    await this.facebook({ action: "sync" }, "Facebook could not be read.");
  }

  async facebookDisconnect() {
    await this.facebook({ action: "disconnect" }, "Facebook could not be disconnected.");
  }

  async webhookState(key: string): Promise<WebhookState> {
    return must(await this.db.rpc("webhook_state", { p_key: key })) as unknown as WebhookState;
  }

  async webhookConnect(key: string): Promise<WebhookState> {
    return must(await this.db.rpc("webhook_connect", { p_key: key })) as unknown as WebhookState;
  }

  async webhookNewAddress(key: string): Promise<WebhookState> {
    return must(await this.db.rpc("webhook_new_address", { p_key: key })) as unknown as WebhookState;
  }

  async webhookSetAssignment(key: string, mode: FacebookAssign["mode"], members: string[]) {
    done(await this.db.rpc("webhook_set_assignment", { p_key: key, p_mode: mode, p_members: members }));
  }

  async webhookSetFields(key: string, fields: WebhookFields): Promise<WebhookRetried> {
    return must(
      await this.db.rpc("webhook_set_fields", { p_key: key, p_fields: fields as Json }),
    ) as unknown as WebhookRetried;
  }

  async webhookRetry(key: string): Promise<WebhookRetried> {
    return must(await this.db.rpc("webhook_retry", { p_key: key })) as unknown as WebhookRetried;
  }

  async indiamartSaveKey(key: string): Promise<IndiaMartRead> {
    return await this.edge<IndiaMartRead>("indiamart", { action: "save_key", key }, "The key could not be saved.");
  }

  async indiamartCheckNow(): Promise<IndiaMartRead> {
    return await this.edge<IndiaMartRead>("indiamart", { action: "check" }, "IndiaMART could not be read.");
  }

  async indiamartRemoveKey() {
    done(await this.db.rpc("indiamart_remove_key"));
  }

  async objectionQuotes(category: string): Promise<ObjectionQuotes> {
    const since = new Date(Date.now() - 7 * 86_400_000).toISOString();
    const [insights, taxonomy] = await Promise.all([
      this.db.from("insights").select("*", { count: "exact" }).eq("kind", "objection").eq("category", category)
        .gt("created_at", since).order("created_at", { ascending: false }).limit(100),
      this.db.from("taxonomies").select("label").eq("kind", "objection").eq("category", category).maybeSingle(),
    ]);
    const rows = must(insights);
    const leadIds = [...new Set(rows.map((r) => r.lead_id).filter((id): id is string => id !== null))];
    const leads = leadIds.length
      ? must(await this.db.from("leads").select("id, name, phone_e164").in("id", leadIds))
      : [];
    const byId = new Map(leads.map((l) => [l.id, l]));
    return {
      category,
      label: maybe(taxonomy)?.label ?? category,
      total: insights.count ?? rows.length,
      rows: rows.map((r) => ({ ...r, lead: (r.lead_id && byId.get(r.lead_id)) || null })),
    };
  }

  async leads(
    query: LeadQuery,
  ): Promise<{ rows: LeadListItem[]; total: number }> {
    const from = query.page * PAGE_SIZE;
    let q = this.db.from("leads").select("*", { count: "exact" });
    const search = (query.search ?? "").replace(/[,()*%\\"]/g, " ").trim();
    if (search) {
      const digits = search.replace(/\D/g, "");
      q = digits.length >= 4
        ? q.or(`name.ilike.*${search}*,phone_e164.like.*${digits}*`)
        : q.ilike("name", `%${search}%`);
    }
    if (query.stage) q = q.eq("stage", query.stage);
    if (query.interest) q = q.eq("interest", query.interest);
    if (query.owner === "none") q = q.is("owner_member_id", null);
    else if (query.owner) q = q.eq("owner_member_id", query.owner);
    if (query.called === "no") q = q.is("last_contact_at", null);
    else if (query.called === "yes") q = q.not("last_contact_at", "is", null);
    // The last contact first; customers never called after them, the newest enquiry first as on the phone's Today,
    // then the newest added. The id last, so the pages keep one order and none repeats or skips a customer (D-150).
    const res = await q
      .order("last_contact_at", { ascending: false, nullsFirst: false })
      .order("last_enquiry_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false })
      .order("id")
      .range(from, from + PAGE_SIZE - 1);
    const leads = must(res);
    const ids = leads.map((l) => l.id);
    const tasks = ids.length
      ? must(
        await this.db.from("tasks").select("*").in("lead_id", ids).eq(
          "status",
          "open",
        ),
      )
      : [];
    const byLead = new Map(tasks.map((t) => [t.lead_id, t]));
    return {
      rows: leads.map((l) => ({ ...l, next_task: byLead.get(l.id) ?? null })),
      total: res.count ?? leads.length,
    };
  }

  async lead(id: string) {
    const [lead, calls, recordings, analyses, tasks, insights] = await Promise
      .all([
        this.db.from("leads").select("*").eq("id", id).single(),
        this.db.from("calls").select("*").eq("lead_id", id).order(
          "started_at",
          { ascending: false },
        ).limit(100),
        this.db.from("recordings").select("*").eq("lead_id", id).order(
          "created_at",
          { ascending: false },
        ).limit(100),
        this.db.from("analyses").select("*").eq("lead_id", id).order(
          "created_at",
          { ascending: false },
        ).limit(50),
        this.db.from("tasks").select("*").eq("lead_id", id).order("due_at", {
          ascending: false,
        }).limit(50),
        this.db.from("insights").select("*").eq("lead_id", id).order(
          "created_at",
          { ascending: false },
        ).limit(100),
      ]);
    return {
      lead: must(lead),
      calls: must(calls),
      recordings: must(recordings),
      analyses: must(analyses),
      tasks: must(tasks),
      insights: must(insights),
    };
  }

  async updateLead(
    id: string,
    patch: {
      stage?: Stage;
      owner_member_id?: string | null;
      name?: string | null;
    },
  ) {
    changedOne(
      await this.db.from("leads").update(patch).eq("id", id).select("id"),
    );
  }

  async eraseLead(id: string, reason: string, neverRecord: boolean) {
    return must(
      await this.db.rpc("erase_lead", { p_lead_id: id, p_reason: reason, p_never_record: neverRecord }),
    ) as unknown as EraseResult;
  }

  // ---------------------------------------------------------- the CRM, as on the phone (D-163)

  async customerList(query: CustomerQuery): Promise<CustomerList> {
    return must(await this.db.rpc("crm_customers", { p_query: customerQueryJson(query) })) as unknown as CustomerList;
  }

  async customerPage(id: string): Promise<CustomerPage> {
    return must(await this.db.rpc("customer_page", { p_lead_id: id })) as unknown as CustomerPage;
  }

  async followUps(query: FollowUpQuery): Promise<FollowUpList> {
    return must(await this.db.rpc("crm_follow_ups", { p_query: followUpQueryJson(query) })) as unknown as FollowUpList;
  }

  async insights(from: string, to: string): Promise<Insights> {
    return must(await this.db.rpc("crm_insights", { p_from: from, p_to: to })) as unknown as Insights;
  }

  async saveFollowUp(input: FollowUpInput): Promise<void> {
    const followUp: Record<string, Json> = {
      type: input.type,
      due_at: input.due_at,
      due_has_time: input.due_has_time,
      device_at: new Date().toISOString(),
    };
    if (input.id) followUp.id = input.id;
    if (input.lead_id) followUp.lead_id = input.lead_id;
    if (input.assigned_to) followUp.assigned_to = input.assigned_to;
    if (input.place?.trim()) followUp.place = input.place.trim();
    if (input.reason?.trim()) followUp.reason = input.reason.trim();
    must(await this.db.rpc("save_follow_up", { p_follow_up: followUp }));
  }

  async completeFollowUp(id: string, result?: VisitResult | null): Promise<void> {
    done(await this.db.rpc("complete_follow_up", { p_task_id: id, p_result: result ?? undefined }));
  }

  async snoozeFollowUp(id: string, untilIso: string): Promise<void> {
    done(await this.db.rpc("snooze_follow_up", { p_task_id: id, p_until: untilIso }));
  }

  async cancelFollowUp(id: string): Promise<void> {
    done(await this.db.rpc("cancel_follow_up", { p_task_id: id }));
  }

  async setStage(leadId: string, stageId: string, reason?: string | null): Promise<void> {
    done(await this.db.rpc("set_stage", { p_lead_id: leadId, p_stage_id: stageId, p_reason: reason || undefined }));
  }

  async setLabel(leadId: string, label: Label | null): Promise<void> {
    done(await this.db.rpc("set_caller_label", { p_lead_id: leadId, p_label: label }));
  }

  async saveCustomerDetails(leadId: string, name: string | null, details: Record<string, string>): Promise<void> {
    done(
      await this.db.rpc("save_customer_details", {
        p_lead_id: leadId,
        p_name: name ?? undefined,
        p_details: details,
      }),
    );
  }

  async addCustomerNumber(leadId: string, phone: string, person?: string, role?: string): Promise<void> {
    done(
      await this.db.rpc("add_customer_number", {
        p_lead_id: leadId,
        p_phone: toE164(phone),
        p_person: person?.trim() || undefined,
        p_role: role?.trim() || undefined,
      }),
    );
  }

  async removeCustomerNumber(numberId: string): Promise<void> {
    done(await this.db.rpc("remove_customer_number", { p_number_id: numberId }));
  }

  async addNote(leadId: string, text: string): Promise<void> {
    done(await this.db.rpc("add_note", { p_lead_id: leadId, p_text: text }));
  }

  async assignCustomers(leadIds: string[], ownerId: string | null): Promise<number> {
    return must(await this.db.rpc("assign_customers", { p_lead_ids: leadIds, p_owner: ownerId }));
  }

  async messageTemplates(): Promise<MessageTemplate[]> {
    const rows = maybe(
      await this.db.from("message_templates").select("id, name, body, link, for_visits").eq("active", true)
        .eq("channel", "whatsapp").order("position").order("name"),
    ) ?? [];
    return rows.map((r) => ({
      id: r.id,
      name: r.name,
      body: (r.body && typeof r.body === "object" && !Array.isArray(r.body) ? r.body : {}) as Record<string, string>,
      link: r.link,
      for_visits: r.for_visits,
    }));
  }

  async saveMessageTemplate(t: MessageTemplateInput): Promise<string> {
    return must(
      await this.db.rpc("save_message_template", {
        p_template: { id: t.id ?? null, name: t.name, body: t.body, link: t.link, for_visits: t.for_visits },
      }),
    );
  }

  async removeMessageTemplate(id: string): Promise<void> {
    done(await this.db.rpc("remove_message_template", { p_id: id }));
  }

  async orderMessageTemplates(ids: string[]): Promise<void> {
    done(await this.db.rpc("order_message_templates", { p_ids: ids }));
  }

  async dispositions(): Promise<Dispositions> {
    const rows = maybe(
      await this.db.from("call_dispositions").select("id, call, label, label_hi, acts_as").eq("active", true)
        .order("position").order("created_at"),
    ) ?? [];
    return {
      answered: parseDispositions(rows.filter((r) => r.call === "answered")),
      unanswered: parseDispositions(rows.filter((r) => r.call === "unanswered")),
    };
  }

  async saveDispositions(call: DispositionCall, list: Disposition[]): Promise<Disposition[]> {
    return parseDispositions(
      must(await this.db.rpc("save_call_dispositions", { p_call: call, p_list: dispositionsJson(list) })),
    );
  }

  async logMessage(leadId: string, templateId: string | null): Promise<void> {
    done(await this.db.rpc("log_message", { p_lead_id: leadId, p_template_id: templateId ?? undefined }));
  }
  async addLead(
    input: { phone: string; name?: string; ownerMemberId?: string },
  ): Promise<string> {
    return must(
      await this.db.rpc("add_lead", {
        p_phone: input.phone,
        p_name: input.name || undefined,
        p_owner_member_id: input.ownerMemberId || undefined,
      }),
    );
  }

  async callDetail(recordingId: string): Promise<CallDetail> {
    const recording = must(
      await this.db.from("recordings").select("*").eq("id", recordingId)
        .single(),
    );
    const [call, lead, transcript, analysis, insights, reviews] = await Promise
      .all([
        recording.call_id
          ? this.db.from("calls").select("*").eq("id", recording.call_id)
            .maybeSingle()
          : null,
        recording.lead_id
          ? this.db.from("leads").select("*").eq("id", recording.lead_id)
            .maybeSingle()
          : null,
        this.db.from("transcripts").select("*").eq("recording_id", recordingId)
          .maybeSingle(),
        this.db.from("analyses").select("*").eq("recording_id", recordingId)
          .order("created_at", { ascending: false })
          .limit(1).maybeSingle(),
        this.db.from("insights").select("*").eq("recording_id", recordingId)
          .order("t", { ascending: true }),
        this.db.from("dispositions").select("*").eq("recording_id", recordingId)
          .order("created_at", {
            ascending: false,
          }),
      ]);
    return {
      recording,
      call: call ? maybe(call) : null,
      lead: lead ? maybe(lead) : null,
      transcript: maybe(transcript),
      analysis: maybe(analysis),
      insights: must(insights),
      reviews: must(reviews),
    };
  }

  async playback(
    recordingId: string,
    t?: number | null,
  ): Promise<{ url: string; t: number | null }> {
    const { data, error } = await this.db.functions.invoke<
      { url: string; t: number | null }
    >("playback", {
      body: { recording_id: recordingId, t: t ?? null },
    });
    if (error) {
      let message = "The recording could not be played.";
      const context = (error as { context?: Response }).context;
      if (context && typeof context.json === "function") {
        const body = await context.json().catch(() => null);
        if (body?.error) message = body.error;
      }
      throw new ApiError(message);
    }
    if (!data) throw new ApiError("The recording could not be played.");
    return data;
  }

  async rawCalls(
    query: { memberId?: string | null; before?: string | null; limit?: number },
  ): Promise<RawCallRow[]> {
    const limit = query.limit ?? RAW_CALLS_PAGE;
    let calls = this.db.from("calls")
      .select(
        "id, member_id, device_id, lead_id, phone_e164, raw_number, contact_name, direction, started_at, duration_s, call_status",
      )
      .order("started_at", { ascending: false }).limit(limit);
    if (query.memberId) calls = calls.eq("member_id", query.memberId);
    if (query.before) calls = calls.lt("started_at", query.before);
    const callRows = must(await calls);
    // Recordings that matched no call, over the same stretch of time.
    let loose = this.db.from("recordings")
      .select(
        "id, member_id, lead_id, status, error_kind, match_method, file_name, recorded_at, created_at, duration_s, audio_deleted_at, erase_requested_at",
      )
      .is("call_id", null).order("created_at", { ascending: false }).limit(limit);
    if (query.memberId) loose = loose.eq("member_id", query.memberId);
    if (query.before) loose = loose.lt("created_at", query.before);
    if (callRows.length === limit) loose = loose.gte("created_at", callRows[callRows.length - 1].started_at);
    const looseRows = must(await loose);
    const linked = callRows.length
      ? must(
        await this.db.from("recordings")
          .select("id, call_id, status, error_kind, match_method, file_name, audio_deleted_at, erase_requested_at")
          .in("call_id", callRows.map((c) => c.id)),
      )
      : [];
    const leadIds = [
      ...new Set([...callRows, ...looseRows].map((r) => r.lead_id).filter((x): x is string => !!x)),
    ];
    const leads = leadIds.length
      ? must(await this.db.from("leads").select("id, name, phone_e164").in("id", leadIds))
      : [];
    const names = new Map((await this.members()).map((m) => [m.id, m.display_name]));
    const leadById = new Map(leads.map((l) => [l.id, l]));
    // When each phone last synced, for a call with no recording: still coming, or missing (D-175).
    const deviceIds = [...new Set(callRows.map((c) => c.device_id).filter((x): x is string => !!x))];
    const synced = new Map(
      (deviceIds.length ? must(await this.db.from("devices").select("id, last_sync_at").in("id", deviceIds)) : []).map((
        d,
      ) => [d.id, d.last_sync_at]),
    );
    const recByCall = new Map(linked.map((r) => [r.call_id, r]));
    const playable = (r: { audio_deleted_at: string | null; erase_requested_at: string | null }) =>
      !r.audio_deleted_at && !r.erase_requested_at;
    const rows: RawCallRow[] = [
      ...callRows.map((c): RawCallRow => {
        const rec = recByCall.get(c.id);
        const lead = c.lead_id ? leadById.get(c.lead_id) : undefined;
        return {
          kind: "call",
          id: c.id,
          at: c.started_at,
          direction: c.direction,
          duration_s: c.duration_s,
          call_status: c.call_status,
          member_id: c.member_id,
          member_name: names.get(c.member_id) ?? "",
          lead_id: c.lead_id,
          lead_name: lead?.name ?? null,
          phone: c.phone_e164 ?? lead?.phone_e164 ?? c.raw_number,
          contact_name: c.contact_name,
          recording: rec
            ? {
              id: rec.id,
              status: rec.status,
              no_conversation: rec.error_kind === "no_conversation",
              match_method: rec.match_method,
              file_name: rec.file_name,
              playable: playable(rec),
            }
            : null,
          recording_due: rec ? null : recordingDue(c, c.device_id ? synced.get(c.device_id) ?? null : null),
        };
      }),
      ...looseRows.map((r): RawCallRow => {
        const lead = r.lead_id ? leadById.get(r.lead_id) : undefined;
        return {
          kind: "recording",
          id: r.id,
          at: r.recorded_at ?? r.created_at,
          direction: null,
          call_status: null,
          duration_s: r.duration_s,
          member_id: r.member_id,
          member_name: names.get(r.member_id) ?? "",
          lead_id: r.lead_id,
          lead_name: lead?.name ?? null,
          phone: lead?.phone_e164 ?? null,
          contact_name: null,
          recording: {
            id: r.id,
            status: r.status,
            no_conversation: r.error_kind === "no_conversation",
            match_method: r.match_method,
            file_name: r.file_name,
            playable: playable(r),
          },
        };
      }),
    ];
    return rows.sort((a, b) => b.at.localeCompare(a.at));
  }

  async recordingCounts(): Promise<RecordingCounts> {
    // Row-level security decides whose recordings are counted, as everywhere else.
    const count = async (statuses: string[] | null, withoutCustomer = false) => {
      let q = this.db.from("recordings").select("id", { count: "exact", head: true });
      if (statuses) q = q.in("status", statuses as Recording["status"][]);
      if (withoutCustomer) q = q.is("lead_id", null);
      const { count: n, error } = await q;
      if (error) throw toApiError(error);
      return n ?? 0;
    };
    const [received, processed, waiting, failed, withoutCustomer] = await Promise.all([
      count(null),
      count(["done"]),
      count(["queued", "held", "processing"]),
      count(["failed"]),
      count(["done"], true),
    ]);
    return { received, processed, waiting, failed, without_customer: withoutCustomer };
  }

  async rerunRecording(recordingId: string, note?: { layer: NoteLayer; text: string }): Promise<void> {
    done(
      await this.db.rpc("rerun_recording", {
        p_recording_id: recordingId,
        ...(note ? { p_note: note.text, p_layer: note.layer } : {}),
      }),
    );
  }

  async callNotes(): Promise<CallNote[]> {
    return (must(await this.db.rpc("call_notes_log", { p_limit: 500 })) as unknown as CallNote[]) ?? [];
  }

  async qaMe(): Promise<QaMe> {
    return must(await this.db.rpc("qa_me")) as unknown as QaMe;
  }

  async noteDashboardVisit() {
    done(await this.db.rpc("note_dashboard_visit"));
  }

  async qaNext(stage: QaStage): Promise<QaNext> {
    return must(await this.db.rpc("qa_next", { p_stage: stage })) as unknown as QaNext;
  }

  async qaCall(recordingId: string): Promise<QaCall> {
    return must(await this.db.rpc("qa_call", { p_recording_id: recordingId })) as unknown as QaCall;
  }

  async qaSave(
    recordingId: string,
    stage: QaStage,
    review: { answers: Record<string, boolean>; note?: string; flag?: boolean; layer?: QaLayer | null },
  ): Promise<{ flagged: boolean; next: QaNext }> {
    return must(
      await this.db.rpc("qa_save", {
        p_recording_id: recordingId,
        p_stage: stage,
        p_answers: review.answers,
        ...(review.note ? { p_note: review.note } : {}),
        p_flag: review.flag ?? false,
        ...(review.layer ? { p_layer: review.layer } : {}),
      }),
    ) as unknown as { flagged: boolean; next: QaNext };
  }

  async qaAddCheck(label: string): Promise<QaCheck> {
    const r = must(await this.db.rpc("qa_add_check", { p_label: label })) as unknown as { key: string; label: string };
    return { ...r, built_in: false };
  }

  async qaSetRole(memberId: string, role: QaRole | null): Promise<void> {
    done(await this.db.rpc("qa_set_role", { p_member_id: memberId, p_qa_role: role }));
  }

  async qaSummary(): Promise<QaSummary> {
    return must(await this.db.rpc("qa_summary")) as unknown as QaSummary;
  }

  async review(
    analysisId: string,
    action: ReviewAction,
    patch?: { type?: TaskType; dueAt?: string },
  ) {
    done(
      await this.db.rpc("review_analysis", {
        p_analysis_id: analysisId,
        p_action: action,
        p_type: patch?.type,
        p_due_at: patch?.dueAt,
      }),
    );
  }

  async openTasks(memberId: string | null): Promise<TaskWithLead[]> {
    let q = this.db.from("tasks").select("*").eq("status", "open");
    if (memberId) q = q.eq("member_id", memberId);
    const tasks: Task[] = must(await q.order("due_at").limit(500));
    const ids = [...new Set(tasks.map((t) => t.lead_id))];
    const leads = ids.length
      ? must(
        await this.db.from("leads").select(
          "id, name, phone_e164, stage, interest",
        ).in("id", ids),
      )
      : [];
    const byId = new Map(leads.map((l) => [l.id, l]));
    return tasks.map((t) => ({ ...t, lead: byId.get(t.lead_id) ?? null }));
  }

  async completeTask(id: string) {
    done(await this.db.rpc("complete_task", { p_task_id: id }));
  }

  async snoozeTask(id: string, minutes: number) {
    done(
      await this.db.rpc("snooze_task", { p_task_id: id, p_minutes: minutes }),
    );
  }

  async rescheduleTask(id: string, dueAt: string, type?: TaskType) {
    done(
      await this.db.rpc("reschedule_task", {
        p_task_id: id,
        p_due_at: dueAt,
        p_type: type,
      }),
    );
  }

  async addTask(
    leadId: string,
    type: TaskType,
    dueAt: string,
    reason?: string,
  ) {
    done(
      await this.db.rpc("add_task", {
        p_lead_id: leadId,
        p_type: type,
        p_due_at: dueAt,
        p_reason: reason || undefined,
      }),
    );
  }

  // ------------------------------------------------------------------ team

  async team() {
    const [members, invitations, devices] = await Promise.all([
      this.db.from("members").select("*").order("display_name"),
      this.db.from("invitations").select("*").is("accepted_at", null).is(
        "revoked_at",
        null,
      )
        .gt("expires_at", new Date().toISOString()).order("created_at", {
          ascending: false,
        }),
      this.db.from("devices").select("*").order("last_seen_at", {
        ascending: false,
      }),
    ]);
    return {
      members: must(members),
      invitations: must(invitations),
      devices: must(devices),
    };
  }

  async teamHealth(): Promise<TeamHealth> {
    return must(await this.db.rpc("team_health")) as unknown as TeamHealth;
  }

  async invite(
    input: { displayName: string; role: Role; phone?: string; email?: string },
  ) {
    done(
      await this.db.rpc("invite_member", {
        p_display_name: input.displayName,
        p_role: input.role,
        p_phone: input.phone || undefined,
        p_email: input.email || undefined,
      }),
    );
  }

  async revokeInvitation(id: string) {
    done(await this.db.rpc("revoke_invitation", { p_invitation_id: id }));
  }

  async revokeDevice(id: string) {
    done(await this.db.rpc("revoke_device", { p_device_id: id }));
  }

  async removeMember(id: string) {
    done(await this.db.rpc("remove_member", { p_member_id: id }));
  }

  async setRole(memberId: string, role: Role) {
    changedOne(
      await this.db.from("members").update({ role }).eq("id", memberId).select(
        "id",
      ),
    );
  }

  // ------------------------------------------------------ import, settings

  async importLeads(rows: ImportRow[], options?: ImportOptions): Promise<ImportResult> {
    return must(
      await this.db.rpc("import_leads", {
        p_rows: rows as unknown as Json,
        ...(options ? { p_options: options as unknown as Json } : {}),
      }),
    ) as unknown as ImportResult;
  }

  async sources() {
    return must(await this.db.from("sources").select("*").order("position"));
  }

  async leadImports(): Promise<LeadImport[]> {
    return must(await this.db.from("lead_imports").select("*").order("created_at", { ascending: false }).limit(20));
  }

  async leadEnquiries(leadId: string): Promise<LeadEnquiry[]> {
    return must(
      await this.db.from("lead_enquiries").select("*").eq("lead_id", leadId)
        .order("enquired_at", { ascending: false }).limit(50),
    );
  }

  async settings() {
    const [org, holidays, excluded] = await Promise.all([
      this.db.from("organizations").select("*").single(),
      this.db.from("holidays").select("*").order("day"),
      this.db.from("excluded_numbers").select("*").order("created_at", {
        ascending: false,
      }),
    ]);
    return {
      org: must(org),
      holidays: must(holidays),
      excluded: must(excluded),
    };
  }

  async saveOrg(
    patch: {
      name?: string;
      timezone?: string;
      working_hours?: Json;
      settings?: Json;
    },
  ) {
    const org = await this.org();
    changedOne(
      await this.db.from("organizations").update(patch).eq("id", org.id).select(
        "id",
      ),
    );
  }

  async addHoliday(day: string, name: string) {
    // Only day and name are granted: the database fills in the org from the signed-in member (guard_org_id).
    done(await this.db.from("holidays").insert({ day, name: name || null } as never));
  }

  async deleteHoliday(id: string) {
    changedOne(
      await this.db.from("holidays").delete().eq("id", id).select("id"),
    );
  }

  async addExcluded(phone: string, reason: string) {
    done(
      await this.db.rpc("add_excluded_number", {
        p_phone: phone,
        p_reason: reason || undefined,
      }),
    );
  }

  async deleteExcluded(id: string) {
    changedOne(
      await this.db.from("excluded_numbers").delete().eq("id", id).select("id"),
    );
  }

  // ------------------------------------------------------- AI instructions

  async aiInstructions(): Promise<AiInstructions> {
    return parseInstructions(must(await this.db.from("ai_instructions").select("*").single()));
  }

  async saveAiInstructions(patch: Partial<Record<TextField, string>> & { checklist?: Json; facts?: Json }) {
    const org = await this.org();
    // Row-level security lets only owners and admins change a row; the database checks the lists (ORG-06).
    changedOne(await this.db.from("ai_instructions").update(patch).eq("org_id", org.id).select("org_id"));
  }

  async objectionCategories() {
    return must(await this.db.from("taxonomies").select("*").eq("kind", "objection").order("sort"));
  }

  async saveObjectionCategory(
    id: string,
    patch: { label?: string; description?: string | null; active?: boolean },
  ) {
    changedOne(await this.db.from("taxonomies").update(patch).eq("id", id).select("id"));
  }

  async addObjectionCategory(label: string, description: string) {
    done(await this.db.rpc("add_objection_category", { p_label: label, p_description: description || undefined }));
  }

  async setCallProfile(memberId: string, patch: { call_type?: CallType | null; ai_note?: string }) {
    changedOne(await this.db.from("members").update(patch).eq("id", memberId).select("id"));
  }

  async setCallBrief(memberId: string, on: boolean) {
    done(await this.db.rpc("set_call_brief", { p_member_id: memberId, p_on: on }));
  }

  async liveCall(): Promise<LiveCall | null> {
    return (maybe(await this.db.rpc("my_live_call")) as unknown as LiveCall | null) ?? null;
  }

  onLiveCall(memberId: string, onChange: () => void): () => void {
    // Realtime applies the row's read policy to each subscriber: only her own call reaches her.
    const channel = this.db
      .channel(`live-call-${memberId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "live_calls", filter: `member_id=eq.${memberId}` },
        () => onChange(),
      )
      .subscribe();
    return () => {
      void this.db.removeChannel(channel);
    };
  }

  onTeamActivity(orgId: string, onChange: () => void): () => void {
    // A synced call updates its customer, and an after-call choice its customer and follow-up; managers may read
    // every lead and task of their company, so Realtime brings them each change (live calls stay each person's own).
    const filter = `org_id=eq.${orgId}`;
    const channel = this.db
      .channel(`team-activity-${orgId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "leads", filter }, () => onChange())
      .on("postgres_changes", { event: "*", schema: "public", table: "tasks", filter }, () => onChange())
      .subscribe();
    return () => {
      void this.db.removeChannel(channel);
    };
  }

  async checklistSummary(days: number): Promise<ChecklistTally[]> {
    return must(await this.db.rpc("checklist_summary", { p_days: days }));
  }

  async setBusinessType(type: BusinessType): Promise<BusinessTypeResult> {
    return must(await this.db.rpc("set_business_type", { p_vertical: type })) as unknown as BusinessTypeResult;
  }

  async usage(): Promise<Usage> {
    return must(await this.db.rpc("usage_summary")) as unknown as Usage;
  }

  async digests() {
    return must(
      await this.db.from("digests").select("*").order("day", {
        ascending: false,
      }).limit(30),
    );
  }

  // -------------------------------------------------------------- platform

  async platformOrgs(): Promise<PlatformOrg[]> {
    return must(await this.db.rpc("platform_list_organizations")).map((o) => ({
      ...o,
      features: addOnsOf(o.features),
      vertical: asBusinessType(o.vertical),
    }));
  }

  async platformAudit() {
    return must(await this.db.rpc("platform_audit_log", { p_limit: 100 }));
  }

  async platformCreateOrg(
    input: {
      name: string;
      ownerName: string;
      ownerEmail?: string;
      ownerPhone?: string;
      vertical: BusinessType;
      addOns: AddOns;
      memberLimit: number | null;
    },
  ) {
    done(
      await this.db.rpc("platform_create_organization", {
        p_name: input.name,
        p_owner_name: input.ownerName,
        p_owner_email: input.ownerEmail || undefined,
        p_owner_phone: input.ownerPhone || undefined,
        p_vertical: input.vertical,
        p_coach: input.addOns.coach,
        p_autopilot: input.addOns.autopilot,
        p_member_limit: input.memberLimit ?? undefined,
      }),
    );
  }

  async platformInviteOwner(
    input: {
      orgId: string;
      ownerName: string;
      ownerEmail?: string;
      ownerPhone?: string;
    },
  ) {
    done(
      await this.db.rpc("platform_invite_owner", {
        p_org_id: input.orgId,
        p_owner_name: input.ownerName,
        p_owner_email: input.ownerEmail || undefined,
        p_owner_phone: input.ownerPhone || undefined,
      }),
    );
  }

  async platformSetStatus(
    orgId: string,
    status: "active" | "suspended",
    reason: string,
  ) {
    done(
      await this.db.rpc("platform_set_organization_status", {
        p_org_id: orgId,
        p_status: status,
        p_reason: reason,
      }),
    );
  }

  async platformApproveOrg(orgId: string) {
    return must(await this.db.rpc("platform_approve_organization", { p_org_id: orgId }));
  }

  async platformSetMinutes(orgId: string, minutes: number, reason: string) {
    done(
      await this.db.rpc("platform_set_ai_minutes", {
        p_org_id: orgId,
        p_minutes: minutes,
        p_reason: reason,
      }),
    );
  }

  async platformSetAddOns(orgId: string, addOns: AddOns, reason: string) {
    return must(
      await this.db.rpc("platform_set_features", {
        p_org_id: orgId,
        p_coach: addOns.coach,
        p_autopilot: addOns.autopilot,
        p_reason: reason,
      }),
    );
  }

  async platformSetMemberLimit(orgId: string, limit: number | null, reason: string) {
    return must(
      await this.db.rpc("platform_set_member_limit", {
        p_org_id: orgId,
        // A null limit is no limit; the generated types only know numbers.
        p_limit: limit as number,
        p_reason: reason,
      }),
    );
  }

  async platformUpdateOrg(
    orgId: string,
    input: { name: string; vertical: BusinessType; timezone: string; defaultCountry: string },
  ) {
    done(
      await this.db.rpc("platform_update_organization", {
        p_org_id: orgId,
        p_name: input.name,
        p_vertical: input.vertical,
        p_timezone: input.timezone,
        p_default_country: input.defaultCountry,
      }),
    );
  }

  // ----------------------------------------------------------------- billing

  async billing() {
    const [summary, invoices] = await Promise.all([
      this.db.rpc("billing_summary"),
      this.db.from("invoices").select("*").order("issued_at", { ascending: false }).limit(50),
    ]);
    return { summary: must(summary) as unknown as BillingSummary, invoices: must(invoices) };
  }

  async buyMinutes(pack: string) {
    done(await this.db.rpc("buy_minutes", { p_pack: pack }));
  }

  async platformHealth(): Promise<PlatformHealth> {
    return must(await this.db.rpc("platform_health")) as unknown as PlatformHealth;
  }

  async platformHealthOverview(): Promise<HealthOverview> {
    return must(await this.db.rpc("platform_health_overview")) as unknown as HealthOverview;
  }

  async platformCompanyHealth(orgId: string): Promise<CompanyHealthDetail> {
    return must(await this.db.rpc("platform_company_health", { p_org_id: orgId })) as unknown as CompanyHealthDetail;
  }

  async platformCompanyTeam(orgId: string): Promise<CompanyTeamMember[]> {
    return must(await this.db.rpc("platform_company_team", { p_org_id: orgId })) as unknown as CompanyTeamMember[];
  }

  async platformSetCompanyFlags(
    orgId: string,
    flags: { internal: boolean; quietUntil: string | null; note: string | null },
  ): Promise<CompanyHealth> {
    return must(
      await this.db.rpc("platform_set_company_flags", {
        p_org_id: orgId,
        p_internal: flags.internal,
        // Null clears them; the generated types only know strings.
        p_quiet_until: flags.quietUntil as string,
        p_note: flags.note as string,
      }),
    ) as unknown as CompanyHealth;
  }

  async platformAiUsage(from: string, to: string, limit = 500): Promise<PlatformAiUsage> {
    return must(
      await this.db.rpc("platform_ai_usage", { p_from: from, p_to: to, p_limit: limit }),
    ) as unknown as PlatformAiUsage;
  }

  async platformSetAiMode(mode: "live" | "paused", reason: string) {
    done(await this.db.rpc("platform_set_ai_mode", { p_mode: mode, p_reason: reason }));
  }

  async platformIntegrationDemand(): Promise<IntegrationDemand[]> {
    return must(await this.db.rpc("platform_integration_demand")) as unknown as IntegrationDemand[];
  }

  async platformInvoices(status?: "issued" | "paid" | "void") {
    return must(await this.db.rpc("platform_invoices", status ? { p_status: status } : {}));
  }

  async platformMarkPaid(invoiceId: string, reference: string) {
    done(await this.db.rpc("platform_mark_invoice_paid", { p_invoice_id: invoiceId, p_reference: reference }));
  }

  async platformVoidInvoice(invoiceId: string, reason: string) {
    done(await this.db.rpc("platform_void_invoice", { p_invoice_id: invoiceId, p_reason: reason }));
  }
}
